import { Badge, Icon, IconLib, Image, Text, View, classNames, getForegroundColor } from '@fold-dev/core'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import { FRONTEER_HEARTBEATS } from '../../App'
import { TbMessageCircle } from 'react-icons/tb'

export const Block = forwardRef((props: any, ref) => {
    const {
        subtle,
        size = 'md',
        color,
        icon,
        name,
        src,
        srcSet,
        fallbackSrc,
        isMain,
        isChannel = false,
        isPrivate = false,
        invert,
        style = {},
        inProjectLayout,
        otherUserId = null,
        ...rest
    } = props
    const isPublicChannel = useMemo(() => !isPrivate && isChannel, [isPrivate, isChannel])
    const { width, height, iconSize, borderRadius, fontSize } = useMemo(() => {
        switch (size) {
            case '2xl':
                return { width: 75, height: 75, iconSize: 'xl', fontSize: '2rem', borderRadius: 'var(--f-radius-xl)' }
            case 'xl':
                return { width: 50, height: 50, iconSize: 'xl', fontSize: '1.5re', borderRadius: 'var(--f-radius-lg)' }
            case 'lg':
                return {
                    width: 34,
                    height: 34,
                    iconSize: 'lg',
                    fontSize: '1.3rem',
                    borderRadius: 'var(--f-radius-lg)',
                }
            case 'md':
                return { width: 30, height: 30, iconSize: 'md', fontSize: '1.1rem', borderRadius: 'var(--f-radius-lg)' }
            case 'sm':
                return { width: 22, height: 22, iconSize: 'md', fontSize: '0.9rem', borderRadius: 'var(--f-radius-md)' }
            case 'xs':
                return { width: 17, height: 17, iconSize: 'sm', fontSize: '0.6rem', borderRadius: 'var(--f-radius-sm)' }
            default:
                return { width: 20, height: 20, iconSize: 'md', fontSize: '1rem', borderRadius: 'var(--f-radius-lg)' }
        }
    }, [size])
    const initials = useMemo(() => {
        return (String(name) || '')
            .split(' ')
            .splice(0, 2)
            .map((str: string) => str.charAt(0))
            .join('')
            .toUpperCase()
    }, [name])
    const styles = useMemo(() => {
        if (color) {
            return invert
                ? {
                      flexShrink: 0,
                      color: getForegroundColor(color),
                      backgroundColor: inProjectLayout || isMain || isPublicChannel ? 'transparent' : color,
                      border: isPublicChannel
                          ? color
                              ? `1px solid rgba(from ${color} r g b / 50%)`
                              : '1px solid var(--f-color-border-strong)'
                          : undefined,
                      ...style,
                  }
                : {
                      flexShrink: 0,
                      color: isMain ? 'var(--f-color-main-icon)' : color,
                      //color:  isMain ? 'var(--f-color-main-icon)' : `color-mix(in srgb, ${color} 10%, var(--f-color-surface))`,
                      backgroundColor:
                          inProjectLayout || isMain
                              ? 'transparent'
                              : `rgb(from ${color} r g b / 15%)`,
                      //: `rgb(from ${color} r g b / 20%)`,
                      border: isPublicChannel
                          ? color
                              ? `1px solid rgba(from ${color} r g b / 10%)`
                              : '1px solid var(--f-color-border-strong)'
                          : color && !isMain
                          ? `1px solid rgb(from ${color} r g b / 10%)`
                          : undefined,
                      ...style,
                  }
        } else {
            return invert
                ? {
                      ...style,
                      backgroundColor: 'var(--f-color-accent)',
                      color: 'var(--f-color-surface)',
                  }
                : {
                      ...style,
                      color: 'var(--f-color-accent)',
                      backgroundColor:
                          inProjectLayout || isMain || isPublicChannel
                              ? 'transparent'
                              : 'rgb(from var(--f-color-accent) r g b / 12.5%)',
                      border: isPublicChannel
                          ? color
                              ? `1px solid rgba(from ${color} r g b / 50%)`
                              : '1px solid var(--f-color-border-strong)'
                          : '1px solid var(--f-color-border)',
                  }
        }
    }, [color, style, invert])
    const className = classNames({ 'block': true, 'f-row': true, 'is-main': isMain }, [props.className])
    const [presence, setPresence] = useState(undefined)
    const presenceVariant = useMemo(() => {
        switch (presence) {
            case 'online':
                return 'success'
            case 'away':
                return 'warning'
            case 'busy':
                return 'danger'
            default:
                return undefined
        }
    }, [presence])

    const handleInterval = () => {
        const userTime = window[FRONTEER_HEARTBEATS][otherUserId]
        const now = new Date().getTime()

        if (!userTime) {
            setPresence(undefined)
        } else {
            if (now - userTime < 5000) setPresence('online')
            if (now - userTime > 5000) setPresence('away')
            if (now - userTime > 10000) setPresence(undefined)
        }
    }

    useEffect(() => {
        handleInterval()
    }, [])

    useEffect(() => {
        const interval = setInterval(handleInterval, 5000)
        return () => clearInterval(interval)
    })

    return (
        <View
            {...rest}
            ref={ref}
            style={styles}
            zIndex={0}
            width={width}
            position="relative"
            height={height}
            radius={isPrivate ? 50 : borderRadius}
            className={className}>
            {/* If there is a picture */}
            {(src || srcSet || fallbackSrc) && (
                <Image
                    objectFit="cover"
                    width="100%"
                    radius={isPrivate ? 50 : borderRadius}
                    height="100%"
                    src={src}
                    srcSet={srcSet}
                    fallbackSrc={fallbackSrc}
                    loader
                />
            )}

            {isChannel && !isPrivate && (
                <View
                    width={22}
                    height={22}
                    radius="var(--f-radius-lg)"
                    row>
                    <Icon
                        size={iconSize}
                        icon={TbMessageCircle}
                        color={color || 'var(--f-color-text-weak)'}
                    />
                </View>
            )}

            {icon && !icon.includes('main:') && !isChannel && (
                <IconLib
                    icon={icon}
                    size={iconSize}
                    color={styles.color}
                />
            )}

            {/* tabler-calendar-icon is a catch-all */}
            {icon && icon.includes('main:') && !isChannel && (
                <IconLib
                    icon={icon}
                    size={iconSize}
                    className="tabler-calendar-icon" 
                />
            )}

            {presence && isPrivate && (
                <Badge
                    variant={presenceVariant}
                    anchor="bottom-right"
                    width={8}
                    height={8}
                    zIndex={10}
                    style={{
                        '--f-badge-dot-distance': '0rem',
                        'outline': 'var(--f-avatar-badge-outline)',
                    }}
                />
            )}

            {/* If there is no picture or icon */}
            {!src && !srcSet && !fallbackSrc && !icon && !isPublicChannel && (
                <Text
                    as="span"
                    fontSize={fontSize}
                    fontWeight={500}
                    color="currentColor">
                    {initials}
                </Text>
            )}

            {/* Otherwise, display children */}
            {props.children}
        </View>
    )
})
